import Cookies from 'universal-cookie';
import { useState, useEffect } from 'react';
import env from './env';

// Narrow isolation: ONLY canary gets a suffixed cookie name. Prod,
// portfolios, QA, and local all keep the plain cookie name they use today.
//
// Why: prod writes `activeAccountId` on `.contently.com`, which the
// browser then sends to `canary.contently.com` via the parent-domain
// rule. When canary also writes `activeAccountId` (on `.canary.contently.com`),
// `document.cookie` ends up with two entries of the same name; the cookie
// parser resolves duplicates last-write-wins with non-deterministic
// ordering, and canary can read prod's account id — which doesn't exist
// in its accounts list — breaking the nav.
//
// Suffixing only the canary cookie name (`activeAccountId__canary`)
// isolates canary reads from prod's parent-domain cookie without touching
// prod, QA, or local behavior.
const envKey = (key) => (env().allDomains === '.canary.contently.com' ? `${key}__canary` : key);

// Wrap universal-cookies get/set with env specific domain information
const jar = {
  cookies() {
    return new Cookies();
  },
  get(key) {
    return this.cookies().get(envKey(key));
  },
  set(key, value) {
    const domain = env().allDomains;
    this.cookies().set(envKey(key), value, { path: '/', domain });
  },
  remove(key) {
    return this.cookies().remove(envKey(key));
  }
};

// Call with a Class to add getters/setters using cookie storage
// Ex: withCookieJar(MyClass, 'aCookieBasedProp', 'anotherOne');
const withCookieJar = (obj, ...args) => {
  [...args].forEach(prop => {
    Object.defineProperty(obj.prototype, prop, {
      set: val => {
        jar.set(prop, val);
      },
      get: () => jar.get(prop)
    });
  });
  return obj;
};

// Custom hook for React components
const useCookieJar = (key, defaultValue) => {
  // Always default to existing cookie val
  let cookieVal = jar.get(key);

  // Safe guard against 'undefined' string values
  cookieVal = cookieVal === 'undefined' ? undefined : cookieVal;

  // set default if available
  cookieVal = cookieVal || defaultValue;

  // Get/Set in memory in addition to in cookie so we can rerender components as needed
  const [value, setValue] = useState(cookieVal);

  // Update cookie when state changes
  useEffect(() => jar.set(key, value), [value, key]);

  // Return just like useState but with remove function
  return [value, setValue, () => jar.remove(key)];
};

export default jar;
export { withCookieJar, useCookieJar };
